// Wires lib/strokeSplit into the monitor: GPS fixes + catch events in,
// smoothed speed out (at each catch in stroke mode, 1 Hz in 6 s fallback mode).
import { useCallback, useEffect, useRef } from 'react';
import { createStrokeSplitSmoother, getSplitSmoothingStrokes } from '@/lib/strokeSplit';

export default function useStrokeSplit({ enabled, sessionActive, demo, gpsPosition, gpsSpeed, onUpdate }) {
  const smRef = useRef(null);
  if (!smRef.current) smRef.current = createStrokeSplitSmoother();
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const demoRef = useRef(demo);
  demoRef.current = demo;

  useEffect(() => {
    if (sessionActive) smRef.current.reset();
  }, [sessionActive]);

  useEffect(() => {
    if (!enabled || !gpsPosition) return;
    smRef.current.addFix(Date.now(), { lat: gpsPosition.lat, lng: gpsPosition.lng, speed: gpsSpeed });
  }, [enabled, gpsPosition, gpsSpeed]);

  useEffect(() => {
    const onCatch = (e) => {
      const d = e.detail || {};
      if (!enabledRef.current || !!d.demo !== !!demoRef.current) return;
      const v = smRef.current.onCatch(d.t || Date.now(), getSplitSmoothingStrokes(), d.expectedMs);
      if (v != null) onUpdateRef.current(v, 'stroke', smRef.current.avgStrokeSplit(3));
    };
    window.addEventListener('crewz-accel-catch', onCatch);
    return () => window.removeEventListener('crewz-accel-catch', onCatch);
  }, []);

  useEffect(() => {
    if (!enabled || !sessionActive) return undefined;
    const id = setInterval(() => {
      const c = smRef.current.current(Date.now());
      if (c.mode === 'time') onUpdateRef.current(c.speed, 'time');
    }, 1000);
    return () => clearInterval(id);
  }, [enabled, sessionActive]);

  // Demo Mode feeds simulated speed directly (no GPS)
  return useCallback((v) => smRef.current.addSpeed(Date.now(), v), []);
}
